import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LiveRefreshProvider } from "@/contexts/LiveRefresh";
import {
    LayoutDashboard,
    ListChecks,
    Users2,
    Building2,
    History,
    LogOut,
    User as UserIcon,
    Sparkles,
    CalendarDays,
    Receipt,
    ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/tasks", label: "Tasks", icon: ListChecks },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/billing", label: "Billing", icon: Receipt },
    { to: "/clients", label: "Clients", icon: Building2 },
    { to: "/users", label: "Users", icon: Users2, adminOnly: true },
    { to: "/my-tasks", label: "My Tasks", icon: ClipboardList, adminOnly: true },
    { to: "/audit", label: "Activity Log", icon: History, adminOnly: true },
];

function initials(name = "") {
    return name
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <LiveRefreshProvider>
        <div className="flex min-h-screen sdc-soft-bg" data-testid="app-layout">
            {/* Sidebar */}
            <aside
                className="hidden lg:flex w-64 flex-col border-r border-border bg-white/70 backdrop-blur"
                data-testid="sidebar"
            >
                <Link to="/" className="flex items-center gap-2 px-6 pt-6 pb-8">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                        <span className="font-display text-base font-extrabold">C</span>
                    </div>
                    <div>
                        <div className="font-display text-lg font-extrabold tracking-tight text-foreground">
                            Credo SDC
                        </div>
                    </div>
                </Link>

                <nav className="flex-1 px-3">
                    {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `group mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                        isActive
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "text-foreground hover:bg-secondary"
                                    }`
                                }
                            >
                                <Icon className="h-4 w-4" />
                                <span className="font-medium">{n.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="m-3 mt-8 rounded-xl border border-border bg-secondary/60 p-4">
                    <div className="flex items-center gap-2 text-primary">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">
                            Tip
                        </span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                        Mark tasks as <span className="font-semibold text-foreground">Completed</span>{" "}
                        — SDC will remind you to bill & add reimbursable expenses.
                    </p>
                </div>
            </aside>

            {/* Main */}
            <div className="flex flex-1 flex-col">
                {/* Topbar */}
                <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-white/70 px-6 backdrop-blur">
                    <div className="flex items-center gap-3 lg:hidden">
                        <div className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground">
                            <span className="font-display text-sm font-extrabold">S</span>
                        </div>
                        <div className="font-display text-lg font-extrabold tracking-tight">
                            SDC
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-3">
                        <div className="hidden md:flex flex-col items-end leading-tight">
                            <div className="text-sm font-medium text-foreground">{user?.name}</div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                {user?.role}
                            </div>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="rounded-full p-0 h-10 w-10"
                                    data-testid="user-menu-trigger"
                                >
                                    <Avatar>
                                        <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                                            {initials(user?.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="font-semibold">{user?.name}</div>
                                    <div className="text-xs font-normal text-muted-foreground">
                                        {user?.email}
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate("/profile")}
                                    data-testid="menu-profile"
                                >
                                    <UserIcon className="mr-2 h-4 w-4" />
                                    Profile & password
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={handleLogout}
                                    data-testid="menu-logout"
                                >
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Mobile nav */}
                <div className="lg:hidden border-b border-border bg-white/70 backdrop-blur">
                    <div className="flex gap-1 overflow-x-auto px-3 py-2">
                        {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
                            const Icon = n.icon;
                            return (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.end}
                                    className={({ isActive }) =>
                                        `flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs ${
                                            isActive
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-secondary text-foreground"
                                        }`
                                    }
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {n.label}
                                </NavLink>
                            );
                        })}
                    </div>
                </div>

                <main className="flex-1 px-4 py-6 md:px-8 md:py-8">
                    <Outlet />
                </main>
            </div>
        </div>
        </LiveRefreshProvider>
    );
}
